import { useState } from 'react';
import { AnimatePresence } from 'motion/react';
import type { IframePoolItem } from '../../../modules/api';
import { ChevronLeft, useIsScrolled } from '../../../modules/design-system';
import { SLEEK_HEADER_SCROLLED, SleekButton } from '../../../modules/theme';
import { TILE_CLASSES } from './tile';
import { WhatsInsideItem } from './WhatsInsideItem';
import { WhatsInsideSheet } from './WhatsInsideSheet';

type Props = {
  items: IframePoolItem[];
  loading?: boolean;
  onBack: () => void;
};

const SKELETON_COUNT = 4;
const PAGE_TITLE = "What's inside";

/**
 * Sleek "What's inside" page — the pack's pull pool as a grid behind the
 * lobby's "What's inside" pill (partner Loom 2026-10-06, Triumph-style).
 * Tapping a card slides up its sheet; the back arrow returns to the lobby.
 */
export function WhatsInsidePage({ items, loading = false, onBack }: Props) {
  const [selectedItem, setSelectedItem] = useState<IframePoolItem | null>(null);
  const scrolled = useIsScrolled(8);

  const tiles =
    items.length > 0
      ? items.map((item) => <WhatsInsideItem key={item.id} item={item} onSelect={setSelectedItem} />)
      : loading
        ? Array.from({ length: SKELETON_COUNT }, (_, slot) => (
            <div key={`skeleton-${slot}`} aria-hidden className={TILE_CLASSES + ' animate-pulse'} />
          ))
        : null;

  return (
    <div data-co-component="WhatsInside" className="@container relative min-h-screen w-full">
      <header
        className={
          'sticky top-0 z-30 header-safe-top ' +
          'transition-[background-color,backdrop-filter,border-color] duration-200 ease-out ' +
          (scrolled ? SLEEK_HEADER_SCROLLED : 'border-b border-transparent')
        }
      >
        <div className="mx-auto grid w-full max-w-350 grid-cols-[40px_minmax(0,1fr)_40px] items-center px-5 py-4 @[600px]:px-6">
          <SleekButton name="SleekBackButton" aria-label="Back" onClick={onBack}>
            <ChevronLeft size={16} />
          </SleekButton>
          <h1 className="m-0 text-center text-[18px] leading-[24px] font-medium tracking-[-0.44px] text-[var(--co-WhatsInside-titleColor,#ffffff)]">
            {PAGE_TITLE}
          </h1>
          <span aria-hidden />
        </div>
      </header>

      <main className="mx-auto w-full max-w-350 px-5 pb-12 @[600px]:px-6">
        <div className="grid grid-cols-2 gap-3 @[600px]:grid-cols-3 @[900px]:grid-cols-4 @[1200px]:grid-cols-5">
          {tiles}
        </div>
      </main>

      <AnimatePresence>
        {selectedItem && <WhatsInsideSheet item={selectedItem} onClose={() => setSelectedItem(null)} />}
      </AnimatePresence>
    </div>
  );
}
